import React, { FC } from "react";
import { IDamsList, IDataTest, ISummaryCriticalHazards, ILikelihood,ISocietalRisk, dataEntry, IHarmSummary } from "../../types/types";
import { Accordion, AccordionSummary, Grid, Paper } from "@material-ui/core";
import DamHeatMap from "./dam_heat_map";
import { ExpandMore } from "@material-ui/icons";
import Typography from "@material-ui/core/Typography";
import CriticalHazardsHeatMap, { IHDataC } from "./critical_hazards_heat_map";
import BarChart from "./bar";
import NoDamInfo from "./no_dam";
import HeatMapTableCH from "./heat_map_table_CH";
import { trafficLight } from "../../theme";
import PieChart from "./pie";


interface ICHProps {
    criticalHazards: ISummaryCriticalHazards[],
    damList: IDamsList[],
    assessments_filtered: number[],
    risk_assessments: ISocietalRisk[]
}



const CriticalHazardsSummary: FC<ICHProps> = ({ criticalHazards, damList, assessments_filtered, risk_assessments }) => {

    const filtered_critical_hazards: Array<ISummaryCriticalHazards> = [];



    assessments_filtered.forEach(id => {
        const filtered = criticalHazards.filter(s => s.assessment_project_id === id)
        filtered.forEach(element => filtered_critical_hazards.push(element))
    })

    let anyDams = false

    if (filtered_critical_hazards.length > 0) {
        anyDams = true
    }

    // for the failure modes that are undefined, set their id to 5
    filtered_critical_hazards.forEach(element => {
        if (!element.societal_confidence_id) {
            element.societal_confidence_id = 5
        }
        if (!element.individual_confidence_id) {
            element.individual_confidence_id = 5
        }
    })


    const subgroups = Array.from(new Set(filtered_critical_hazards.map(
        s => s.societal_confidence_id)))

    const assessment_ids = Array.from(new Set(filtered_critical_hazards.map(
        s => s.assessment_project_id)))

    const levels = [1, 2, 3, 4, 5]

    const level_names = ["very strong", "strong", "weak", "very weak", "undefined"]

    const societal_levels = assessment_ids.map(a => {

        const data = filtered_critical_hazards.filter(c => c.assessment_project_id === a)
        // return min adequacy level
        const min_level = Math.max(...data.map(c => c.societal_confidence_id))
        return min_level
    })

    const individual_levels = assessment_ids.map(a => {

        const data = criticalHazards.filter(c => c.assessment_project_id === a)
        // return min adequacy level
        const min_level = Math.max(...data.map(c => c.individual_confidence_id))
        return min_level
    })

    const get_hazards_summary = (catergory: string): IDataTest<dataEntry> => {

        let specific_data: number[] = []

        if (catergory === "Societal") {
            specific_data = societal_levels
        }
        else if (catergory === "Individual") {
            specific_data = individual_levels
        }

        return {
            title: `Confidence below ${catergory} Safety Threshold`,
            description: ["Individual Risk Confidence", "Societal Confidence"],
            data: [{ name: 'Very Strong', value: [specific_data.filter(d => d === 1).length] },
            { name: 'Strong', value: [specific_data.filter(d => d === 2).length] },
            { name: 'Weak', value: [specific_data.filter(d => d === 3).length] },
            { name: 'Very Weak', value: [specific_data.filter(d => d === 4).length] },
            { name: 'Undefined', value: [specific_data.filter(d => d === 5).length] }],
            colour: {
                background: [trafficLight.vgreen, trafficLight.bgreen, trafficLight.byellow, trafficLight.bred, trafficLight.grey],
                font: [trafficLight.green, trafficLight.green, trafficLight.yellow, trafficLight.red, trafficLight.dgrey]
            },
            height: 300,
        }

    }

    const dam_list = assessment_ids.map(a_id => {
        return damList.filter(dam => dam.id === a_id)[0].dam.name
    })

    const getLocation = (subgroup: number, assessment_id: number) => {
        const subgroup_index = levels.indexOf(subgroup)
        const assessment_index = assessment_ids.indexOf(assessment_id)
        return [subgroup_index, assessment_index]
    }


    const get_hazards_summary_matrix = (key: keyof ISummaryCriticalHazards, name: string): IHDataC => {
        const assessments: number[][] = []

        const assessment = assessment_ids.map(a => {
            return {
                id: a,
                levels: levels.map(l => {
                    return {
                        level: l,
                        count: filtered_critical_hazards.filter(c => c.assessment_project_id === a && c[key] === l).length
                    }
                })
            }
        })

        assessment.forEach(s => {
            return levels.forEach(l => {
                const xy = getLocation(l, s.id)
                let level = s.levels.find(le => le.level === l)?.count
                if (level !== undefined) {
                    if (level > 0) {
                        assessments.push([xy[0], xy[1], level, l])
                    }
                }
            })

        })

        return {
            title: name,
            data: assessments,

        }
    }



    // critial hazards combined
    const one = get_hazards_summary_matrix("societal_confidence_id", "Societal Risk Confidence")
    const two = get_hazards_summary_matrix("individual_confidence_id", "Individual Risk Confidence")

    return (
        <>
            <Accordion>
                <AccordionSummary id={"1"} expandIcon={<ExpandMore />}>
                    <Typography variant="h6"> Summary </Typography>
                </AccordionSummary>
                <Typography>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                                <div style={{ padding: "10px" }}>
                                    <Paper variant={"outlined"} style={{ textAlign: 'left', height:'50px', display:'flex', alignItems:'center' }}>

                                    The pie charts show the number of dams with their confidence of being below the Safety Thresholds (worst case grouped failure mode presented). 
                                    
                                    </Paper>
                                </div>
                            </Grid>
                    <Grid container spacing={3} xl={12} direction="row">
                        {(<>
                            <Grid item xs={12} sm={12} md={6} lg={6}>
                                {anyDams
                                    ? <PieChart data={get_hazards_summary('Societal')} />
                                    : <NoDamInfo />}

                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={6}>
                                {anyDams
                                    ? <PieChart data={get_hazards_summary('Individual')} />
                                    : <NoDamInfo />}
                            </Grid>
                        </>)}

                    </Grid>
                </Typography>
            </Accordion>

            <Accordion>
                <AccordionSummary id={"2"} expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Detail</Typography>
                </AccordionSummary>
                <Typography>
                    <Grid container spacing={3} xl={12} direction="row">
                        <Grid item xs={12} sm={12} md={12} lg={12}>
                            {anyDams
                                ? <HeatMapTableCH criticalHazards={filtered_critical_hazards} damList={damList} risk_assessments={risk_assessments} />
                                : <NoDamInfo />}
                        </Grid>
                    </Grid>
                </Typography>
            </Accordion>
        </>
    );
}

export default CriticalHazardsSummary;
